import { Button, Typography } from '@breetometer/components';
import { FiatIcon, StatusIcon } from 'BreetAssets';
import {
	AllWalletBalances,
	Amount,
	ContentWrapper,
	DataTable,
	HeaderCTA,
	type ISelectInputOption,
	type ITableColumn,
	sanitizeAndCheckEmpty,
	Seo,
	TableFilter,
	TransactionsMobileColumn,
} from 'BreetComponents';
import {
	alwaysRefetchApiQueryOptions,
	capitalizeFirstLetters,
	type CURRENCYCODETYPE,
	CURRENCYCONFIG,
	formatDateTime,
	getTransactionStatus,
	type ITransactionHistory,
	SORT_LATEST_TO_OLDEST,
	type TransactionHistoryType,
} from 'BreetHelpers';
import { withPaginatedFilters, type WithPaginatedFiltersProps } from 'BreetHOC';
import {
	type ITableFilterField,
	useComponentVisibilityApi,
	useCurrencyOutput,
	useModalApi,
	useTableFilters,
	useWindowSize,
} from 'BreetHooks';
import { apiSlice, useAppDispatch, useGetTransactionsEditServicesQuery } from 'BreetRedux';
import isEmpty from 'lodash/isEmpty';
import { useCallback, useMemo } from 'react';

import TableColumnTransactionDetails from './components/table-columns/TransactionDetails';
import TableColumnType from './components/table-columns/Type';
import { getWalletTypeOptions, type IWalletTransactionProps, WALLET_STATUS_OPTIONS } from './WalletTransactions.helpers';

const WalletsTransactions = ({
	reqFilter,
	reqSearch,
	reqSorting,
	reqPageNum,
	reqPageSize,
	handleReqSearch,
	handlePageChange,
	applyFilters,
	filterProps,
	pageTitle,
	hideWalletBalance,
	seoPage,
	tip,
	exportsQuery,
	onlyWithdrawals,
}: Partial<WithPaginatedFiltersProps<ITransactionHistory<TransactionHistoryType>>> & IWalletTransactionProps) => {
	const TRANSACTION_PAYOUT_OPTIONS: ISelectInputOption[] = useMemo(
		() => [
			{
				label: capitalizeFirstLetters(CURRENCYCONFIG.USD.code),
				value: CURRENCYCONFIG.USD.code.toLowerCase(),
			},
			{
				label: capitalizeFirstLetters(CURRENCYCONFIG.NGN.code),
				value: CURRENCYCONFIG.NGN.code.toLowerCase(),
			},
			{
				label: capitalizeFirstLetters(CURRENCYCONFIG.GHS.code),
				value: CURRENCYCONFIG.GHS.code.toLowerCase(),
			},
		],
		[]
	);

	const filterFields = useMemo<ITableFilterField[]>(() => {
		const defaultOptions = [
			{ key: 'type', label: 'Type', options: getWalletTypeOptions(onlyWithdrawals) },
			{ key: 'status', label: 'Status', options: WALLET_STATUS_OPTIONS },
		];

		return !onlyWithdrawals
			? defaultOptions
			: [...defaultOptions, { key: 'currency', label: 'Payout Type', options: TRANSACTION_PAYOUT_OPTIONS }];
	}, [TRANSACTION_PAYOUT_OPTIONS, onlyWithdrawals]);

	const syncFilters = useCallback(
		(values: Record<string, string>) => applyFilters?.({ status: values.status, currency: values.currency, type: values.type }),
		[applyFilters]
	);

	const {
		filterItems,
		activeFilters,
		isFiltered,
		clearAll,
		values: filterValues,
	} = useTableFilters(filterFields, syncFilters, reqFilter);

	const settlementValue = filterValues.type;

	const { openModalDirect } = useModalApi();

	const dispatch = useAppDispatch();

	const withdrawalsQuery = onlyWithdrawals
		? '?services=withdrawals&services=conversions'
		: '?services=sells&services=withdrawals&services=edits&services=conversions&services=topups';

	const filterData = useMemo(() => {
		// `type` is a services selector, not a backend filter key — keep it out of the `filter` param.
		const filterableReqFilter = Object.fromEntries(Object.entries(reqFilter ?? {}).filter(([key]) => key !== 'type'));
		return {
			page: reqPageNum,
			size: reqPageSize,
			defaultParams: !settlementValue ? withdrawalsQuery : '',
			filter: sanitizeAndCheckEmpty({ ...filterableReqFilter, ...reqSearch, ...filterProps }),
			sort: sanitizeAndCheckEmpty({ ...reqSorting, createdAt: reqSorting?.createdAt ?? SORT_LATEST_TO_OLDEST }),
			...(settlementValue ? { services: settlementValue } : {}),
		};
	}, [reqPageNum, reqPageSize, settlementValue, withdrawalsQuery, reqFilter, reqSearch, filterProps, reqSorting]);

	const {
		isLoading: withdrawalHistoryLoading,
		isFetching: withdrawalHistoryFetching,
		isError: withdrawalHistoryIsError,
		data: withdrawalHistoryData,
	} = useGetTransactionsEditServicesQuery(filterData, alwaysRefetchApiQueryOptions);

	useGetTransactionsEditServicesQuery(
		{ ...filterData, startDate: exportsQuery?.startDate ?? '', endDate: exportsQuery?.endDate ?? '', exports: true },
		{ ...alwaysRefetchApiQueryOptions, skip: !exportsQuery?.exports }
	);

	const withdrawalHistory = withdrawalHistoryData?.data ?? [];

	const windowSize = useWindowSize();

	const currencyOutput = useCurrencyOutput();

	const isMobile = windowSize.isClient && windowSize.width <= 768;

	const viewWithdrawalHistoryDetails = useCallback(
		(id: string) => () => {
			openModalDirect({ id: 'withdrawalHistoryDetails', metadata: { id }, variant: 'right-sheet' });
		},
		[openModalDirect]
	);

	const viewTransactionDetails = useCallback(
		(id: string) => () => {
			openModalDirect({ id: 'transactionDetails', metadata: { id }, variant: 'right-sheet' });
		},
		[openModalDirect]
	);

	const { hideComponentOnDesktop, hideComponentOnMobile } = useComponentVisibilityApi();

	const handleTableRowClick = useCallback(
		({ row }: { row: ITransactionHistory<TransactionHistoryType> }) => {
			if (row.collection === 'sells') {
				viewTransactionDetails(row._id)();
				return;
			}
			viewWithdrawalHistoryDetails(row._id)();
		},
		[viewWithdrawalHistoryDetails, viewTransactionDetails]
	);

	const transactionTableCol = useMemo<ITableColumn<ITransactionHistory<TransactionHistoryType>>[]>(
		() => [
			{
				key: 'doc._id',
				searchKey: 'docId',
				title: 'Reference',
				copy: true,
				copyMessage: 'Reference copied successfully',
				copyIconPosition: 'after',
				upperCase: true,
				onClick: handleTableRowClick,
				...hideComponentOnMobile,
				width: 170,
			},
			{
				key: 'doc.meta.type',
				title: 'Type',
				onClick: handleTableRowClick,
				...hideComponentOnMobile,
				render: ({ row }) => <TableColumnType row={row} />,
			},
			{
				key: 'amount',
				title: 'Amount',
				...hideComponentOnMobile,
				onClick: handleTableRowClick,
				render: ({ row }) => (
					<div className='transaction-payout-cell'>
						<Typography as='p'>
							<Amount>{currencyOutput(row.amount)}</Amount>
						</Typography>
						<div className='currency-amount'>
							<FiatIcon
								variant='round'
								currencyCode={row.currency.toUpperCase() as CURRENCYCODETYPE}
							/>
							<Typography
								as='span'
								className='color-primary-100'
							>
								{row.currency.toUpperCase()}
							</Typography>
						</div>
					</div>
				),
			},
			{
				key: 'status',
				title: 'Status',
				onClick: handleTableRowClick,
				render: ({ row }) => <StatusIcon status={getTransactionStatus(row.status)} />,
				...hideComponentOnMobile,
			},
			{
				key: 'createdAt',
				title: 'Created On',
				onClick: handleTableRowClick,
				render: ({ row }) => <Typography as='p'>{formatDateTime(row.createdAt)}</Typography>,
				...hideComponentOnMobile,
			},
			{
				key: '_id',
				width: 200,
				onClick: handleTableRowClick,
				// No inner onClick: the cell's handleTableRowClick already opens the correct modal by
				// collection. An inner handler would both double-fire (open two modals) and mis-route
				// conversions to the withdrawal modal.
				render: ({ row }) => <TableColumnTransactionDetails row={row} />,
				...hideComponentOnDesktop,
			},
			{
				key: 'status',
				onClick: handleTableRowClick,
				render: ({ row }) => (
					<TransactionsMobileColumn.Right
						amount={currencyOutput(row.amount)}
						currency={row.currency.toUpperCase() as CURRENCYCODETYPE}
						status={getTransactionStatus(row.status)}
					/>
				),
				...hideComponentOnDesktop,
			},
			{
				key: 'extra',
				render: ({ row }) => (
					<div className='table-button'>
						<Button
							label='View'
							onClick={row.collection === 'sells' ? viewTransactionDetails(row._id) : viewWithdrawalHistoryDetails(row._id)}
							variant='tertiary'
							className='border-tertiary-400 tertiary-semi-hover'
						/>
					</div>
				),
				...hideComponentOnMobile,
			},
		],
		[
			currencyOutput,
			handleTableRowClick,
			hideComponentOnDesktop,
			hideComponentOnMobile,
			viewTransactionDetails,
			viewWithdrawalHistoryDetails,
		]
	);

	const isSearchFilter = !isEmpty(reqSearch) || !isEmpty(reqFilter);

	const onRefresh = useCallback(() => {
		dispatch(apiSlice.util.invalidateTags(['AllTransactions', 'GetUserWallets']));
	}, [dispatch]);

	const renderFilterDropdownWithLocalState = useCallback(
		(toggleVisibility?: () => void) => (
			<TableFilter
				filterItems={filterItems}
				onResetFilter={toggleVisibility}
			/>
		),
		[filterItems]
	);

	return (
		<>
			<Seo page={seoPage} />

			<div className='datatable bodycontent'>
				{!hideWalletBalance && (
					<>
						<HeaderCTA /> <AllWalletBalances />
					</>
				)}

				<ContentWrapper
					hideBreadcrumb
					allowOverflow
					className={hideWalletBalance ? '' : 'wallet-transaction-table'}
				>
					<DataTable
						columns={transactionTableCol}
						data={withdrawalHistory}
						isError={withdrawalHistoryIsError}
						isLoading={withdrawalHistoryLoading}
						hideHeader={isMobile}
						onPageChange={handlePageChange}
						paginate={withdrawalHistoryData?.meta}
						docItemsIdentifier='transactions'
						isSearchFilter={isSearchFilter}
						onRefresh={onRefresh}
						tip={tip}
						overflowMode='contained'
						headerProps={{
							pageTitle,
							isFilter: isFiltered,
							activeFilters,
							onClearFilters: clearAll,
							filterProps: {
								dropDownComponent: renderFilterDropdownWithLocalState,
							},
							counter: 0,
							isLoading: withdrawalHistoryLoading || withdrawalHistoryFetching,
							exportProps: true,
							searchProps: {
								handleReqSearch,
								placeHolder: 'Search by Reference ID',
								searchKey: 'doc._id',
							},
						}}
					/>
				</ContentWrapper>

				{!hideWalletBalance && <HeaderCTA bottom />}
			</div>
		</>
	);
};

const WalletsWithHandlers = withPaginatedFilters(WalletsTransactions);

export default WalletsWithHandlers;
